/* eslint-disable complexity */
import { css, html } from 'lit';

import { BILL_TYPES } from '../../../../../packages/neb-api-client/src/mappers/patient-case-mapper';
import { DEFAULT_TOGGLE_ICON } from '../../../../../packages/neb-lit-components/src/components/neb-toggle-button';
import NebTable, {
  ELEMENTS as BASE_ELEMENTS,
} from '../../../../../packages/neb-lit-components/src/components/tables/neb-table';
import '../../../../../packages/neb-lit-components/src/components/inputs/neb-select';
import '../../../../../packages/neb-lit-components/src/components/inputs/neb-textfield';
import '../../../../../packages/neb-lit-components/src/components/field-groups/neb-modifiers';
import '../../../../../packages/neb-lit-components/src/components/neb-cell-fee-schedule';
import { centsToCurrency } from '../../../../../packages/neb-utils/formatters';
import { number } from '../../../../../packages/neb-utils/masks';
import { BILL_TYPE } from '../../../../../packages/neb-utils/neb-ledger-util';
import {
  getDiagnosisItemHeight,
  ITEM_MIN_WIDTH,
} from '../../../../utils/diagnoses';

export const ELEMENTS = {
  ...BASE_ELEMENTS,
  addButton: {
    selector: '[id^=add-button-]',
  },
  row: {
    selector: '[id^=row-]',
  },
  modifiers: {
    selector: '.modifiers',
  },
  units: {
    selector: '.units',
  },
  feeSchedule: {
    selector: '.fee-schedule',
  },
  diagnosisPointers: {
    selector: '.diagnosis-pointers',
  },
  postAllButton: {
    id: 'post-all-button',
  },
  postButtons: {
    selector: '[id^=post-button-]',
  },
};

const UNPOST_ICON = 'neb:return';
const POSTED_ICON = 'neb:checkmark';
const ALL_POSTED_LABEL = {
  UNPOST: 'Unpost All',
  POST: 'Post All',
  ALL_POSTED: 'All Posted',
};

class NebTableCheckOutEncounterCharges extends NebTable {
  static get properties() {
    return {
      wrapModifiers: { type: Boolean, reflect: true },
      didBillingInfoChange: Boolean,
      diagnoses: Array,
      initialUnits: Array,
      billType: String,
      feeScheduleId: String,
      hasAutoPostCharges: Boolean,
      patientPackageId: String,
      patientPackageChargeIds: Array,
    };
  }

  initState() {
    super.initState();

    this.wrapModifiers = false;
    this.didBillingInfoChange = true;
    this.hasAutoPostCharges = false;
    this.billType = BILL_TYPES.SELF;
    this.feeScheduleId = '';
    this.patientPackageId = '';
    this.patientPackageChargeIds = [];
    this.diagnoses = [];
    this.initialUnits = [];
    this.config = this.__calcConfig();

    this.onToggleAllPosted = () => {};
  }

  initHandlers() {
    super.initHandlers();
    this.handlers = {
      ...this.handlers,
      resize: () => {
        const windowSize = window.innerWidth;

        this.wrapModifiers = windowSize < 1200;

        this.config = this.__calcConfig();
      },
      togglePosted: (isToggled, index) => {
        const name = `chargeDetails.${index}.posted`;

        this.onChange({
          name,
          value: isToggled,
        });

        this.requestUpdate();
      },
      toggleAllPosted: () => {
        this.onToggleAllPosted(!this.__isAllPosted());

        this.requestUpdate();
      },
    };
  }

  __calcConfig() {
    const descriptionBaseConfig =
      'display: -webkit-box; -webkit-box-orient: vertical; overflow: hidden;';

    return [
      {
        key: 'posted',
        label: 'Post All',
        mobile: true,
      },
      {
        key: 'procedure',
        label: 'Procedure',
        flex: css`0 0 70px`,
        customStyle: 'word-break: break-word;',
        mobile: true,
      },
      {
        key: 'description',
        label: 'Description',
        flex: css`1 0 85px`,
        customStyle:
          this.layout !== 'small'
            ? `${descriptionBaseConfig} -webkit-line-clamp: 3;`
            : descriptionBaseConfig,
        mobile: true,
      },
      {
        key: 'modifiers',
        label: 'Modifiers',
        flex: this.wrapModifiers ? css`0 0 120px` : css`0 0 225px`,
      },
      {
        key: 'units',
        label: 'Units',
        flex: css`1 0 40px`,
      },
      {
        key: 'unitCharge',
        label: 'Unit Chrg',
        flex: css`1 0 80px`,
        formatter: v => centsToCurrency(v),
      },
      {
        key: 'billedAmount',
        label: 'Billed',
        flex: css`1 0 60px`,
      },
      {
        key: 'feeScheduleCharges',
        label: 'Fee Schedule',
        flex: css`1 0 70px`,
      },
      {
        key: 'carePackage',
        label: 'Care Pkg',
        flex: css`1 0 30px`,
      },
      {
        key: 'diagnosisPointers',
        label: 'Diagnosis Pointer',
        flex: css`1 0 100px`,
      },
    ];
  }

  __getFeeScheduleModel(
    charge,
    didUnitsChange,
    hasBillingInfoChanged,
    feeScheduleId,
  ) {
    if (charge.encounterChargeId && !didUnitsChange && !hasBillingInfoChanged) {
      return (
        charge.feeScheduleCharges.chartingValues || { feeScheduleName: '' }
      );
    }

    const feeScheduleModel = charge.feeScheduleCharges[feeScheduleId];

    return feeScheduleModel || { feeScheduleName: '' };
  }

  static get styles() {
    return [
      super.styles,
      css`
        :host(:not([wrapModifiers])) .modifiers {
          --modifier-columns: repeat(4, 50.4px);
        }

        :host([wrapModifiers]) .modifiers {
          --modifier-columns: repeat(2, 50.4px);
        }

        .units {
          --text-indent: 8px;
          width: 50px;
        }

        .reorder-spacer {
          flex: 0 0 20px;
          width: 20px;
        }

        .button-post {
          display: flex;
          min-width: 127px;
          max-width: 127px;
          white-space: nowrap;
        }

        :host(:not([layout='small'])) .cell-data {
          padding: 0;
        }
      `,
    ];
  }

  connectedCallback() {
    window.addEventListener('resize', this.handlers.resize);
    super.connectedCallback();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handlers.resize);
    super.disconnectedCallback();
  }

  firstUpdated() {
    this.config = this.__calcConfig();
  }

  __shouldHidePostedButton(isAllPosted) {
    return !this.model.length || (isAllPosted && this.hasAutoPostCharges);
  }

  __renderHeader() {
    const reorder = this.reorder !== false;

    return !this.hideHeader
      ? html`
          <div
            id="${ELEMENTS.header.id}"
            class="row row-header"
            part="row-header"
            ?preview="${this.preview}"
          >
            ${this.__renderSpacerCell(reorder, 'reorder-spacer')}
            ${this.__renderSelectAll()} ${this.__renderConfigHeaderCells()}
            ${this.__renderRemoveAll()} ${this.__renderModeHeaderCell()}
          </div>
        `
      : '';
  }

  __isAllPosted() {
    return this.model.every(charge => charge.posted);
  }

  __getButtonText(posted) {
    if (!posted) return 'Post';

    return this.hasAutoPostCharges ? 'Posted' : 'Unpost';
  }

  __getPostedButtonIcon() {
    return this.hasAutoPostCharges ? POSTED_ICON : UNPOST_ICON;
  }

  __getAllPostedButtonText(allPosted) {
    if (!allPosted) return ALL_POSTED_LABEL.POST;

    return this.hasAutoPostCharges
      ? ALL_POSTED_LABEL.ALL_POSTED
      : ALL_POSTED_LABEL.UNPOST;
  }

  __getAllPostedButtonIcon(allPosted) {
    return allPosted && !this.hasAutoPostCharges
      ? UNPOST_ICON
      : DEFAULT_TOGGLE_ICON;
  }

  __isButtonDisabled(posted) {
    if (!posted) return false;

    return this.hasAutoPostCharges;
  }

  renderHeaderCell(columnConfig) {
    switch (columnConfig.key) {
      case 'posted': {
        const allPosted = this.__isAllPosted();

        if (this.__shouldHidePostedButton(allPosted)) {
          return '';
        }

        return html`
          <neb-toggle-button
            id="${ELEMENTS.postAllButton.id}"
            class="button-post"
            .canToggle="${true}"
            .isToggled="${allPosted}"
            .buttonText="${this.__getAllPostedButtonText(allPosted)}"
            .toggledIcon="${this.__getAllPostedButtonIcon(allPosted)}"
            .disabled="${this.__isButtonDisabled(allPosted)}"
            .onClick="${this.handlers.toggleAllPosted}"
          ></neb-toggle-button>
        `;
      }

      default:
        return super.renderHeaderCell(columnConfig);
    }
  }

  renderDataCell(value, columnConfig, rowIndex, name, error) {
    const charge = this.model[rowIndex];

    switch (columnConfig.key) {
      case 'posted': {
        if (this.__shouldHidePostedButton(this.__isAllPosted())) {
          return '';
        }

        return html`
          <neb-toggle-button
            id="post-button-${rowIndex}"
            class="button-post"
            .index="${rowIndex}"
            .canToggle="${true}"
            .isToggled="${value}"
            .buttonText="${this.__getButtonText(value)}"
            .toggledIcon="${this.__getPostedButtonIcon()}"
            .disabled="${this.__isButtonDisabled(value)}"
            .onClick="${this.handlers.togglePosted}"
          ></neb-toggle-button>
        `;
      }

      case 'modifiers':
        return html`
          <neb-modifiers
            class="modifiers"
            label=" "
            helper=" "
            name="${name}"
            .errors="${error}"
            .values="${value}"
            .onChange="${this.handlers.change}"
          ></neb-modifiers>
        `;

      case 'units':
        return html`
          <neb-textfield
            name="${name}"
            class="units"
            helper=" "
            maxLength="3"
            label=" "
            .inputMode="${'numeric'}"
            .error="${error}"
            .value="${value}"
            .mask="${number}"
            .onChange="${this.handlers.change}"
          ></neb-textfield>
        `;

      case 'billedAmount': {
        const didUnitsChange =
          this.initialUnits[rowIndex] !== this.model[rowIndex].units;

        const { feeScheduleCharge } = this.__getFeeScheduleModel(
          charge,
          didUnitsChange,
          this.didBillingInfoChange,
          this.feeScheduleId,
        );

        if (feeScheduleCharge) {
          return centsToCurrency(feeScheduleCharge * charge.units);
        }

        return centsToCurrency(charge.unitCharge * charge.units);
      }

      case 'feeScheduleCharges': {
        const didUnitsChange =
          this.initialUnits[rowIndex] !== this.model[rowIndex].units;

        const feeScheduleModel = this.__getFeeScheduleModel(
          charge,
          didUnitsChange,
          this.didBillingInfoChange,
          this.feeScheduleId,
        );

        const model = {
          ...feeScheduleModel,
          units: charge.units,
        };

        return html`
          <neb-cell-fee-schedule
            class="fee-schedule"
            .model="${model}"
          ></neb-cell-fee-schedule>
        `;
      }

      case 'carePackage': {
        if (this.billType !== BILL_TYPE.PACKAGE || !this.patientPackageId) {
          return '';
        }

        return this.patientPackageChargeIds.includes(charge.chargeId)
          ? 'Y'
          : 'N';
      }

      case 'diagnosisPointers':
        return this.diagnoses.length
          ? html`
              <neb-select
                class="diagnosis-pointers"
                maxSelection="4"
                label=" "
                helper=" "
                .name="${name}"
                .error="${error}"
                .value="${value}"
                .items="${this.diagnoses}"
                .onChange="${this.handlers.change}"
                forceAlignMenu="right"
                placeholder="Select"
                multiSelect
                showFullText
                wrapText
                itemHeight="${getDiagnosisItemHeight(this.diagnoses)}"
                itemMinWidth="${ITEM_MIN_WIDTH}"
              ></neb-select>
            `
          : html` <span class="text-no-diagnoses">No Encounter Dx</span> `;

      default:
        return value;
    }
  }
}

customElements.define(
  'neb-table-check-out-encounter-charges',
  NebTableCheckOutEncounterCharges,
);
